/**
 * Add Text panel — licensed fonts rasterized into printSafeArea (Phase 7).
 */

/**
 * @param {HTMLElement} root
 * @param {{ getState: () => Record<string, unknown>, subscribe: (fn: (state: Record<string, unknown>) => void) => void }} store
 * @param {(payload: {
 *   text: string,
 *   font: string,
 *   color: string,
 *   align: string,
 *   size: string,
 *   overlay: boolean,
 *   side: 'front'|'back'
 * }) => Promise<void>} onApply
 */
export function bindAddTextPanel(root, store, onApply) {
    const pane = root.querySelector('[data-bag-pane="text"]');
    const inputEl = root.querySelector('#bag-text-input');
    const countEl = root.querySelector('#bag-text-count');
    const applyBtn = root.querySelector('#bag-text-btn');
    const overlayEl = root.querySelector('#bag-text-overlay');
    const statusEl = root.querySelector('#bag-text-status');

    if (!pane || !applyBtn || !inputEl) {
        return {
            setStatus() {},
        };
    }

    const maxChars = Math.max(1, Number(pane.getAttribute('data-max-chars') || 80));
    let font = 'sans';
    let size = 'md';
    let align = 'center';
    let color = '#1A1A1A';

    const firstFont = root.querySelector('[data-bag-text-font].is-active');
    if (firstFont && firstFont.getAttribute('data-bag-text-font')) {
        font = firstFont.getAttribute('data-bag-text-font');
    }
    const firstColor = root.querySelector('[data-bag-text-color].is-active');
    if (firstColor && firstColor.getAttribute('data-bag-text-color')) {
        color = firstColor.getAttribute('data-bag-text-color');
    }

    root.querySelectorAll('[data-bag-text-font]').forEach((chip) => {
        chip.addEventListener('click', () => {
            font = chip.getAttribute('data-bag-text-font') || 'sans';
            root.querySelectorAll('[data-bag-text-font]').forEach((el) => {
                el.classList.toggle('is-active', el === chip);
            });
        });
    });

    root.querySelectorAll('[data-bag-text-size]').forEach((chip) => {
        chip.addEventListener('click', () => {
            size = chip.getAttribute('data-bag-text-size') || 'md';
            root.querySelectorAll('[data-bag-text-size]').forEach((el) => {
                el.classList.toggle('is-active', el === chip);
            });
        });
    });

    root.querySelectorAll('[data-bag-text-align]').forEach((chip) => {
        chip.addEventListener('click', () => {
            align = chip.getAttribute('data-bag-text-align') || 'center';
            root.querySelectorAll('[data-bag-text-align]').forEach((el) => {
                el.classList.toggle('is-active', el === chip);
            });
        });
    });

    root.querySelectorAll('[data-bag-text-color]').forEach((chip) => {
        chip.addEventListener('click', () => {
            color = chip.getAttribute('data-bag-text-color') || '#1A1A1A';
            root.querySelectorAll('[data-bag-text-color]').forEach((el) => {
                el.classList.toggle('is-active', el === chip);
            });
        });
    });

    inputEl.addEventListener('input', () => {
        syncCount();
    });
    syncCount();

    let busy = false;

    applyBtn.addEventListener('click', () => {
        if (applyBtn.disabled || busy) {
            return;
        }
        const text = String(inputEl.value || '').trim();
        if (!text) {
            setStatus('Please enter the text you want to print.', true);
            return;
        }
        const side = /** @type {'front'|'back'} */ (store.getState().activeSide || 'front');
        busy = true;
        applyBtn.disabled = true;
        setStatus('Placing text in the print-safe area…', false);
        onApply({
            text: text,
            font: font,
            color: color,
            align: align,
            size: size,
            overlay: !overlayEl || overlayEl.checked,
            side: side,
        })
            .catch((err) => {
                setStatus(err && err.message ? err.message : 'Could not apply text.', true);
            })
            .finally(() => {
                busy = false;
                applyBtn.disabled = Boolean(store.getState().busy);
            });
    });

    // Global busy lock: another panel holds a mutation in flight.
    store.subscribe((state) => {
        applyBtn.disabled = busy || Boolean(state.busy);
    });

    function syncCount() {
        if (!countEl) {
            return;
        }
        countEl.textContent = String(Math.min(maxChars, String(inputEl.value || '').length));
    }

    /**
     * @param {string} message
     * @param {boolean} isError
     */
    function setStatus(message, isError) {
        if (!statusEl) {
            return;
        }
        statusEl.textContent = message;
        statusEl.classList.toggle('text-danger', isError);
        statusEl.classList.toggle('text-muted', !isError);
    }

    return { setStatus };
}
